import { mat3, mat4, quat, vec3 } from "gl-matrix";

export class Camera {
  name: string;
  position: vec3;
  rotation: quat;
  moveSpeed: number;
  sensitivity: number;
  move: boolean;
  rateX: number;
  rateY: number;
  keyboard: { [key: string]: boolean };
  viewMatrix: mat4;

  _onRender: (render: boolean) => void;

  movementKeys = new Set(["KeyW", "KeyA", "KeyS", "KeyD"]);

  constructor(onRender: (render: boolean) => void, camera: Partial<Camera> = {}) {
    this.name = camera.name ?? "Camera";
    this.position = camera.position ?? vec3.fromValues(0, 0, 0);
    this.rotation = camera.rotation ?? quat.create(); // identity quaternion
    this.moveSpeed = 5;
    this.sensitivity = 0.009;
    this.move = false;
    this.rateX = 0;
    this.rateY = 0;
    this.keyboard = {};
    this.viewMatrix = mat4.create();
    this._onRender = onRender;
  }

  refresh() {
    this._onRender(true);
    // Use a short delay to toggle it back off
    requestAnimationFrame(() => {
      this._onRender(this.move);
    });
  }

  getViewMatrix(): mat4 {
    const view = mat4.create();

    // Create a transformation matrix from position and rotation
    const transform = mat4.create();
    mat4.fromRotationTranslation(transform, this.rotation, this.position);

    // Invert it to get the view matrix
    mat4.invert(view, transform);

    this.viewMatrix = view;
    return view;
  }

  lookAt(target: vec3) {
    const forward = vec3.fromValues(0, 0, -1); // camera default forward
    const toTarget = vec3.subtract(vec3.create(), target, this.position);
    vec3.normalize(toTarget, toTarget);

    // Compute the rotation from forward to toTarget
    const dot = vec3.dot(forward, toTarget);

    if (dot < -0.999999) {
      // 180-degree turn
      this.rotation = quat.setAxisAngle(quat.create(), [0, 1, 0], Math.PI);
    } else if (dot > 0.999999) {
      // Same direction
      quat.identity(this.rotation);
    } else {
      const axis = vec3.cross(vec3.create(), forward, toTarget);
      vec3.normalize(axis, axis);
      const angle = Math.acos(dot);
      quat.setAxisAngle(this.rotation, axis, angle);
    }
    this.refresh();
  }

  init(canvas: HTMLCanvasElement) {
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());

    canvas.addEventListener("mousemove", (event) => {
      if (event.buttons === 2) {
        this.move = true;
        this.rateX = event.movementX;
        this.rateY = -event.movementY;

        const deltaYaw = -this.rateX * this.sensitivity;
        const deltaPitch = this.rateY * this.sensitivity;

        const yawQuat = quat.setAxisAngle(quat.create(), [0, 1, 0], deltaYaw);
        const pitchQuat = quat.setAxisAngle(quat.create(), [1, 0, 0], deltaPitch);
        quat.multiply(this.rotation, yawQuat, this.rotation);
        quat.multiply(this.rotation, this.rotation, pitchQuat);
        this.refresh();
      }
    });

    canvas.addEventListener("mouseup", () => {
      this.move = false;
      this.rateX = 0;
      this.rateY = 0;
    });

    canvas.addEventListener("mouseout", () => {
      this.move = false;
      this.rateX = 0;
      this.rateY = 0;
    });

    window.addEventListener("blur", () => {
      this.move = false;
      this.rateX = 0;
      this.rateY = 0;
      this.keyboard = {};
    });

    window.addEventListener("keydown", (event) => {
      if (document.activeElement !== canvas) {
        return;
      }

      if (this.movementKeys.has(event.code)) {
        this.keyboard[event.code] = true;
        this.move = true;
        this.refresh();
      }
    });

    window.addEventListener("keyup", (event) => {
      if (this.movementKeys.has(event.code)) {
        this.keyboard[event.code] = false;
      }
      // Check if any movement key is still down
      this.move = Object.entries(this.keyboard).some(([key, down]) =>
        this.movementKeys.has(key) && down
      );
    });
  }

  update(deltaTime: number) {
    const direction = vec3.create();
    const forward = vec3.fromValues(0, 0, -1);
    const right = vec3.fromValues(1, 0, 0);

    const forwardRotated = vec3.transformQuat(vec3.create(), forward, this.rotation);
    const rightRotated = vec3.transformQuat(vec3.create(), right, this.rotation);

    if (this.keyboard["KeyW"]) vec3.add(direction, direction, forwardRotated);
    if (this.keyboard["KeyS"]) vec3.sub(direction, direction, forwardRotated);
    if (this.keyboard["KeyA"]) vec3.sub(direction, direction, rightRotated);
    if (this.keyboard["KeyD"]) vec3.add(direction, direction, rightRotated);

    if (vec3.length(direction) > 0) {
      vec3.normalize(direction, direction);
      const move = vec3.scale(vec3.create(), direction, this.moveSpeed * 0.01);
      vec3.add(this.position, this.position, move);
      this.refresh();
    }
  }

  rotationFromForward(forward: vec3, up: vec3 = [0, 1, 0]): quat {
    // Create a lookAt matrix pointing from origin to forward
    const viewMatrix = mat4.create();
    mat4.targetTo(viewMatrix, [0, 0, 0], forward, up);

    // Extract rotation (upper-left 3x3)
    const rotMat = mat3.create();
    mat3.fromMat4(rotMat, viewMatrix);

    // Convert rotation matrix to quaternion
    const q = quat.create();
    quat.fromMat3(q, rotMat);
    quat.normalize(q, q);
    return q;
  }

  getForward(viewMatrix: mat4): vec3 {
    const forward = vec3.fromValues(
      -viewMatrix[2],  // -Z row
      -viewMatrix[6],
      -viewMatrix[10]
    );
    vec3.normalize(forward, forward);
    return forward;
  }

  static getQuatFromViewMatrix(viewMatrix: mat4): quat {
    const rotMat = mat3.create();
    mat3.fromMat4(rotMat, viewMatrix); // extract rotation
    const q = quat.create();
    quat.fromMat3(q, rotMat);
    quat.invert(q, q); // invert because view matrix is inverse of camera rotation
    quat.normalize(q, q);
    return q;
  }

  export(): ExportedCamera {
    const camForward = this.getForward(this.viewMatrix);

    return {
      name: this.name,
      position: [this.position[0], this.position[1], this.position[2]],
      front: [camForward[0], camForward[1], camForward[2]],
      up: [0, 1, 0],
      yaw: 90,
      pitch: 0,
      roll: 0,
      viewMatrix: this.viewMatrix,
      moveSpeed: this.moveSpeed,
      sensitivity: this.sensitivity,
    };
  }
}

export type ExportedCamera = {
  name: string;
  position: number[];
  front: number[];
  up: number[];
  pitch: 0;
  yaw: 90;
  roll: 0;
  viewMatrix: mat4;
  moveSpeed: number;
  sensitivity: number;
};
